/* eslint-disable react/style-prop-object */
import { useUserContext } from "../../../providers/UserProvider";
import { LoadingIndicator } from "../LoadingIndicator/LoadingIndicator";

import { Unauthorized } from "../../pages/Unauthorized/Unauthorized";
import { useContextUsers } from "../../../hooks/useContextUsers/useContextUsers";

import UserTable from "../UserList/UserTable";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import styles from "../../pages/UserList/UserList.module.scss";
import { SearchInput } from "../SearchInput/SearchInput";
import { useDebounce } from "../../../hooks/useDebounce/useDebounce";
import {
    ATTRIBUTE_ROLES,
    CONTEXT_SCOPES,
    DEFAULT_DEBOUNCE,
    DEFAULT_PAGE_SIZE,
    EVENT_TYPES
} from "../../../constants/constants";
import { ResetFilterPrompt } from "../ResetFilterPrompt/ResetFilterPrompt";
import { Sidebar } from "../Sidebar/Sidebar";
import { useFormContext } from "../UserForm/UserFormContext/UserFormContext";
import { User } from "../../../domain/User";
import { ConfirmationDialog } from "../ConfirmationDialog/ConfirmationDialog";
import { datadogRum } from "@datadog/browser-rum";
import { InviteUserButtonAndSidebar } from "../InviteUserButtonAndSidebar/InviteUserButtonAndSidebar";
import { AccessManagementUserForm } from "./AccessManagementUserForm/AccessManagementUserForm";
import { useAccessFlowUserFormContext } from "./AccessManagementUserForm/AccessManagementUserFormProvider";
import { UnsavedChangesDialog } from "../UnsavedChangesDialog/UnsavedChangesDialog";
import { Button, Disclose, Dropdown, PaginationControls, Spinner, Typography } from "@optimizely/axiom";
import { useContextRoles } from "../../../hooks/useContextRoles/useContextRoles";
import { useContextProjects } from "../../../hooks/useContextProjects/useContextProjects";
import { useProducts } from "../../../hooks/useProducts/useProducts";
import { ActivationStatusesModal } from "../ActivationStatusesTable/ActivationStatusesModal";
import { emitToast } from "../../../lib/toaster-utils";
import { useAnalyticsTracking } from "../../../hooks/useAnalyticsTracking/useAnalyticsTracking";
import { getBulkUserActionPayload, getRemoveUserPayload, getRevokeUserPayload } from "../../../lib/analytics-helpers";
import { ANALYTICS_EVENT_NAMES, ANALYTICS_FLOWS } from "../../../constants/analytics-constants";
import { Role } from "../../../domain/Role";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag/useFeatureFlag";
import { Flags } from "../../../feature-flags/flags";
import { useUpdateSync } from "../../../hooks/useUpdateSync/useUpdateSync";
import LimitByRole from "../LimitByRole/LimitByRole";

export const AccessManagementUsers = ({
    disableDeleteFromOptiId = false,
    hideRevoke = false
}: {
    disableDeleteFromOptiId?: boolean;
    hideRevoke?: boolean;
}) => {
    const { pathname } = useLocation();
    const { productId, instanceId, projectId } = useParams();
    const { organizationId = "", accessContext, setAccessContext } = useUserContext();
    const { enabled: enableBatchDeletion } = useFeatureFlag(Flags.ENABLE_BATCH_USER_DELETION);
    const { enabled: enableVirtualAgentUsers } = useFeatureFlag(Flags.ENABLE_VIRTUAL_AGENT_USERS);
    const { fireChangeEvent } = useUpdateSync();
    /* istanbul ignore next */
    const segmentFlow = pathname.includes("user-management/users")
        ? ANALYTICS_FLOWS.USERS
        : ANALYTICS_FLOWS.PRODUCT_ACCESS;

    const { products, isLoading: areProductsLoading, error: productsError } = useProducts({ organizationId });
    const instance = products?.flatMap((p) => p.instances).find((inst) => inst.id === instanceId);

    const { activeField, setActiveField } = useAccessFlowUserFormContext();
    const [confirmationError, setConfirmationError] = useState<string>("");
    const [failedUsers, setFailedUsers] = useState<User[]>([]);
    const [usersToRemoveAccess, setUsersToRemoveAccess] = useState<User[] | undefined>();
    const [usersToDelete, setUsersToDelete] = useState<User[] | undefined>();
    const [inProgress, setInProgress] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState<User[]>([]);

    const { accessManagementUserState, updateAccessManagementUserState } = useFormContext();
    const { user: selectedUser } = accessManagementUserState;

    const [selectedRoleFilter, setSelectedRoleFilter] = useState<string | undefined>();
    const [selectedProjectFilter, setSelectedProjectFilter] = useState<string | undefined>();
    const [showActivationStatusesModal, setShowActivationStatusesModal] = useState<boolean>(false);
    const { sendTrackEvent } = useAnalyticsTracking();

    const {
        debouncedValue: debouncedSearchQuery,
        value: searchQuery,
        setValue: setSearchQuery
    } = useDebounce<string>("", DEFAULT_DEBOUNCE);

    /* istanbul ignore next */
    const { users, isLoading, error, currentPage, setCurrentPage, totalCount, removeUserAccess, deleteUser } =
        useContextUsers({
            organizationId,
            context: accessContext,
            roleIdFilter: selectedRoleFilter,
            projectIdFilter: selectedProjectFilter,
            includeExternalStatus: true,
            isVirtual: enableVirtualAgentUsers ? false : undefined,
            searchQuery: debouncedSearchQuery.length > 2 ? debouncedSearchQuery : ""
        });

    const { roles } = useContextRoles({ organizationId, context: accessContext });
    const { projects } = useContextProjects({ context: accessContext });

    const isExp = productId === process.env.REACT_APP_EXPERIMENTATION_PRODUCT_ID;

    const roleFilterSelected = (value?: string) => {
        if (currentPage !== 1) {
            setCurrentPage(1);
        }
        setSelectedRoleFilter(value);
    };

    const projectFilterSelected = (value?: string) => {
        if (currentPage !== 1) {
            setCurrentPage(1);
        }
        setSelectedProjectFilter(value);
    };

    const closeSideNav = useCallback(() => {
        updateAccessManagementUserState({ user: null, editing: false });
    }, [updateAccessManagementUserState]);

    const hasDefaultUserFilters = debouncedSearchQuery.length < 2 && !selectedRoleFilter && !selectedProjectFilter;
    const noDefaultFilterUsers = !isLoading && hasDefaultUserFilters && !users?.length;

    useEffect(() => {
        window.document.addEventListener("confirmation-dialog:name:discard-changes", closeSideNav);
        return () => {
            window.document.removeEventListener("confirmation-dialog:name:discard-changes", closeSideNav);
        };
    }, [closeSideNav]);

    useEffect(() => {
        return () => setConfirmationError("");
    }, [usersToDelete, usersToRemoveAccess]);

    useEffect(() => {
        return closeSideNav;
    }, [currentPage, closeSideNav]);

    const orgUsersBaseRoutes =
        pathname === "/access/users" ||
        pathname === "/access/users/" ||
        pathname === "/user-management/users" ||
        pathname === "/user-management/users/";

    const updateUsersData = ({ eventType, users }: { eventType: string; users: User[] }) => {
        fireChangeEvent({
            type: eventType,
            data: {
                users: users?.map((u) => ({ email: u.email }))
            }
        });
    };

    useEffect(() => {
        if (orgUsersBaseRoutes) {
            setAccessContext({ scope: CONTEXT_SCOPES.ORGANIZATION, items: [{ id: organizationId }] });
        } else if (productId && !instanceId && !projectId) {
            /* istanbul ignore next */
            setAccessContext({ scope: CONTEXT_SCOPES.PRODUCT, items: [{ id: productId }] });
        } else if (productId && instanceId && !projectId) {
            setAccessContext({ scope: CONTEXT_SCOPES.INSTANCE, items: [{ id: instanceId }] });
        } else if (projectId) {
            setAccessContext({ scope: CONTEXT_SCOPES.PROJECT, items: [{ id: projectId }] });
        } else {
            setAccessContext(undefined);
        }
    }, [orgUsersBaseRoutes, setAccessContext, productId, instanceId, projectId, organizationId]);

    const isBulkAction = selectedUsers.length > 1;

    /* istanbul ignore next */
    const handleDeletion = () => {
        const targetUsers = failedUsers?.length > 0 ? failedUsers : usersToDelete;
        if (!targetUsers?.length) return;
        setInProgress(true);
        Promise.all(
            targetUsers.map((user) =>
                deleteUser({ skipRevalidation: enableBatchDeletion, user })
                    .then(() => {
                        if (!isBulkAction) {
                            const payload = getRemoveUserPayload({
                                email: user.email,
                                flow: ANALYTICS_FLOWS.USER_PAGE
                            });
                            sendTrackEvent(payload);
                            setUsersToDelete(undefined);
                            emitToast({ message: "User successfully deleted." });
                        }
                    })
                    .catch((error) => {
                        datadogRum.addError(new Error("FAILED TO DELETE USER"), { error, email: user.email });
                        if (!isBulkAction) {
                            setConfirmationError("An error occurred while deleting user");
                        }
                        return user; // Return the user that failed
                    })
            )
        )
            .then((failedUsersEntries) => {
                const failed = failedUsersEntries.filter(Boolean); // Filter out successful deletions
                const failedCount = failed.length;
                const payload = getBulkUserActionPayload({
                    action: ANALYTICS_EVENT_NAMES.BULK_REMOVE_USERS,
                    emails: targetUsers.filter((u) => !failed.find((f) => f?.email === u.email)).map((u) => u.email),
                    failedCount,
                    flow: segmentFlow,
                    retry: failedUsers.length > 0
                });
                sendTrackEvent(payload);
                if (failed.length > 0) {
                    setFailedUsers(failed as User[]);
                } else {
                    updateUsersData({ eventType: EVENT_TYPES.BULK_DELETE_USER, users: targetUsers || [] });
                    emitToast({ message: "All users successfully deleted." });
                    setSelectedUsers([]);
                    setFailedUsers([]);
                    setUsersToDelete(undefined);
                }
            })
            .finally(() => {
                setInProgress(false);
            });
    };

    /* istanbul ignore next */
    const handleRemoveAccess = () => {
        const targetUsers = failedUsers?.length > 0 ? failedUsers : usersToRemoveAccess;
        if (!targetUsers?.length) return;
        if (targetUsers && accessContext) {
            setInProgress(true);

            Promise.all(
                targetUsers.map((user) =>
                    removeUserAccess({ skipRevalidation: enableBatchDeletion, user })
                        .then((successful) => {
                            if (!isBulkAction) {
                                const payload = getRevokeUserPayload({
                                    email: user.email,
                                    flow: ANALYTICS_FLOWS.USER_PAGE
                                });
                                sendTrackEvent(payload);
                                setUsersToRemoveAccess(undefined);
                                emitToast({ message: "Access removed successfully." });
                                return;
                            }
                            if (!successful) {
                                return user;
                            }
                            return false;
                        })
                        .catch((error) => {
                            datadogRum.addError(new Error("FAILED TO REMOVE USER ACCESS"), {
                                error,
                                email: user.email
                            });
                            if (!isBulkAction) {
                                // throw an error and catch it for the legacy behavior
                                throw new Error("Failed to remove user access.");
                            }
                            return user; // Return the user that failed
                        })
                )
            )
                .then((failedUsersEntries) => {
                    const failed = failedUsersEntries.filter(Boolean); // Filter out successful deletions
                    const failedCount = failed.length;
                    const payload = getBulkUserActionPayload({
                        action: ANALYTICS_EVENT_NAMES.BULK_REVOKE_USERS,
                        emails: targetUsers
                            .filter((u) => !failed.find((f) => f && f.email === u.email))
                            .map((u) => u.email),
                        failedCount,
                        flow: segmentFlow,
                        retry: failedUsers.length > 0
                    });
                    sendTrackEvent(payload);
                    if (failed.length > 0) {
                        setFailedUsers(failed as User[]);
                    } else {
                        updateUsersData({ eventType: EVENT_TYPES.BULK_DELETE_USER, users: targetUsers || [] });
                        emitToast({ message: "Access removed successfully for all users." });
                        setSelectedUsers([]);
                        setUsersToRemoveAccess(undefined);
                    }
                })
                .catch(() => {
                    if (!isBulkAction) {
                        setConfirmationError("An error occurred while removing access for user");
                    }
                })
                .finally(() => {
                    setInProgress(false);
                });
        }
    };
    const handleFormCancel = () => {
        setActiveField({ name: null, hasChanged: false });
        if (!activeField?.hasChanged) {
            closeSideNav();
        }
    };

    /* istanbul ignore next */
    const goToPage = (page: number) => {
        setCurrentPage(page);
    };

    const totalPages = Math.ceil(totalCount / DEFAULT_PAGE_SIZE);
    const showPagination = totalPages > 1;

    if (!accessContext) return <Unauthorized />;

    if (
        !areProductsLoading &&
        !productsError &&
        [CONTEXT_SCOPES.INSTANCE, CONTEXT_SCOPES.PROJECT].indexOf(accessContext.scope) > -1 &&
        !instance?.optiIdEnabled
    )
        return (
            <div className="push-double--top">
                {accessContext.scope === CONTEXT_SCOPES.PROJECT
                    ? "This project's instance must be Opti Id Enabled in order to add users."
                    : "This instance must be Opti ID Enabled in order to add users."}
            </div>
        );

    if (error) return <div className="push-double--top">Error fetching users list.</div>;

    const getRoleName = (role?: Role) => {
        if (!role) return "Unknown";

        const { displayName, isInstanceRole, name = "" } = role;

        if (accessContext?.scope === CONTEXT_SCOPES.PROJECT) {
            return isInstanceRole ? `${displayName} (All Projects)` : displayName;
        }

        return name;
    };

    /* istanbul ignore next */
    const handleDeletionCancellation = () => {
        const hasPartialDeletion = failedUsers.length > 0 && failedUsers.length < usersToDelete!.length;
        if (hasPartialDeletion) {
            const updatedUsers = usersToDelete!.filter((u) => !failedUsers.find((failed) => failed.email === u.email));
            updateUsersData({
                eventType: EVENT_TYPES.BULK_DELETE_USER,
                users: updatedUsers
            });
            // Since bulk deletion causes a partial refresh, but we don't have persisted pagination available,
            // we'll reset the selected users. In order to not have to do this we should implement query state pagination.
            setSelectedUsers([]);
        }
        setFailedUsers([]);
        setUsersToDelete(undefined);
    };

    /* istanbul ignore next */
    const handleRevokeCancellation = () => {
        const hasPartialDeletion = failedUsers.length > 0 && failedUsers.length < usersToRemoveAccess!.length;
        if (hasPartialDeletion) {
            const updatedUsers = usersToRemoveAccess!.filter(
                (u) => !failedUsers.find((failed) => failed.email === u.email)
            );
            updateUsersData({
                eventType: EVENT_TYPES.BULK_DELETE_USER,
                users: updatedUsers
            });
            // Since bulk deletion causes a partial refresh, but we don't have persisted pagination available,
            // we'll reset the selected users. In order to not have to do this we should implement query state pagination.
            setSelectedUsers([]);
        }
        setFailedUsers([]);
        setUsersToRemoveAccess(undefined);
    };
    const singleUserAction = usersToDelete?.length === 1 || usersToRemoveAccess?.length === 1;
    let removeAccessCTA: string = disableDeleteFromOptiId ? "Delete" : "Remove Access";
    /* istanbul ignore next */
    if (failedUsers?.length > 0) {
        removeAccessCTA = disableDeleteFromOptiId ? "Retry Deleting" : "Retry Removing Access";
    }

    let removeTitle: string = "";
    /* istanbul ignore next */
    if (singleUserAction) {
        removeTitle = disableDeleteFromOptiId ? "Delete User" : "Remove Access for User";
    } else {
        removeTitle = disableDeleteFromOptiId ? "Delete Users" : "Remove Access for Users";
    }
    /* istanbul ignore next */
    const deletionTitle = singleUserAction ? "Delete User" : "Delete Users";
    /* istanbul ignore next */
    const deletionCTA = failedUsers?.length > 0 ? "Retry Deleting" : deletionTitle;
    /* istanbul ignore next */
    const revokeAccessCountText = singleUserAction ? (
        "this user"
    ) : (
        <span className="weight--bold">{usersToRemoveAccess?.length} users</span>
    );

    /* istanbul ignore next */
    const renderFailResults = ({ action }: { action: "delete" | "revoke" }) => {
        const message = action === "revoke" ? `Failed to remove ` : `Failed to delete `;
        return (
            enableBatchDeletion &&
            !inProgress &&
            failedUsers?.length > 0 && (
                <div className="background--bad-news border-bad-news border-radius push-double--top push-double--bottom">
                    <Disclose title={`${message} ${failedUsers.length} user(s):`}>
                        {failedUsers.map((u) => (
                            <div className="push-quad--left" key={u.email}>
                                {u.email}
                            </div>
                        ))}
                    </Disclose>
                </div>
            )
        );
    };

    /* istanbul ignore next */
    const getAdditionalDeletionVerbiage = () => {
        return (
            enableBatchDeletion && (
                <span>
                    &nbsp;This action will permanently remove them from your organization in Opti ID, and they will lose
                    access to all previously assigned product access.
                </span>
            )
        );
    };

    /* istanbul ignore next */
    const getBatchButtons = () => {
        return (
            enableBatchDeletion && (
                <>
                    {!hideRevoke && (
                        <Button
                            isDisabled={!selectedUsers.length}
                            style="danger"
                            className="push--right"
                            onClick={() => setUsersToRemoveAccess(selectedUsers)}
                        >
                            Revoke Access...
                        </Button>
                    )}
                    <LimitByRole mode="hide" action={ATTRIBUTE_ROLES.USERS.UPDATE}>
                        <Button
                            isDisabled={!selectedUsers.length}
                            style="danger"
                            className="push--right"
                            onClick={() => setUsersToDelete(selectedUsers)}
                        >
                            Delete Users...
                        </Button>
                    </LimitByRole>
                </>
            )
        );
    };

    /* istanbul ignore next */
    const getDeletionCTA = () => {
        return enableBatchDeletion ? deletionCTA : "Delete";
    };

    return (
        <>
            {!!usersToDelete?.length && (
                <ConfirmationDialog
                    callToActionText={getDeletionCTA()}
                    error={confirmationError}
                    loading={inProgress}
                    onCancel={handleDeletionCancellation}
                    onConfirmation={handleDeletion}
                    title={deletionTitle}
                >
                    Are you sure you want to delete{" "}
                    {singleUserAction ? (
                        "this user"
                    ) : (
                        /* istanbul ignore next */
                        <span className="weight--bold">{usersToDelete?.length} users</span>
                    )}
                    ?{getAdditionalDeletionVerbiage()}
                    {inProgress && (
                        <div className="push-triple--ends">
                            <Spinner size="small" /> Deleting users...
                        </div>
                    )}
                    {renderFailResults({ action: "delete" })}
                </ConfirmationDialog>
            )}

            {!!usersToRemoveAccess?.length && (
                <ConfirmationDialog
                    callToActionText={removeAccessCTA}
                    error={confirmationError}
                    loading={inProgress}
                    onCancel={handleRevokeCancellation}
                    onConfirmation={handleRemoveAccess}
                    title={removeTitle}
                >
                    <Typography type="body">
                        {disableDeleteFromOptiId ? (
                            <>
                                <p>
                                    Are you sure you want to permanently remove {revokeAccessCountText} from your
                                    organization?
                                </p>
                                This action cannot be undone and may impact associated accounts and data.
                            </>
                        ) : (
                            <>
                                Are you sure you want to remove access to the {accessContext.scope} for{" "}
                                {revokeAccessCountText}?
                            </>
                        )}
                        {inProgress && (
                            <div className="push-triple--ends">
                                <Spinner size="small" />{" "}
                                {disableDeleteFromOptiId ? "Deleting users..." : "Revoking Access..."}
                            </div>
                        )}
                        {renderFailResults({ action: "revoke" })}
                    </Typography>
                </ConfirmationDialog>
            )}
            <div className={`list-page ${styles["user-list"]}`}>
                <div className={styles["user-list__bar"]}>
                    <div className={styles["user-list__leftgroup"]}>
                        <div className={styles["user-list__search"]}>
                            <SearchInput
                                placeholder="Search users by name or email..."
                                type="text"
                                value={searchQuery}
                                onChange={({ value }) => {
                                    if (currentPage !== 1) {
                                        setCurrentPage(1);
                                    }
                                    setSearchQuery(value);
                                }}
                                disabled={noDefaultFilterUsers}
                            />
                        </div>
                        <div className={styles["user-list__filter"]}>
                            {(accessContext?.scope === CONTEXT_SCOPES.INSTANCE ||
                                accessContext?.scope === CONTEXT_SCOPES.PROJECT) && (
                                <Dropdown
                                    className="push--left"
                                    buttonContent={{
                                        label: "Role",
                                        content: selectedRoleFilter
                                            ? getRoleName(roles.find((r) => r.id === selectedRoleFilter))
                                            : "All"
                                    }}
                                    style="plain"
                                    arrowIcon="down"
                                    width={350}
                                    isDisabled={noDefaultFilterUsers}
                                >
                                    <Dropdown.Contents>
                                        <Dropdown.ListItem role="option" key="All">
                                            <Dropdown.BlockLink isLink onClick={() => roleFilterSelected(undefined)}>
                                                <Dropdown.BlockLinkText text="All" />
                                            </Dropdown.BlockLink>
                                        </Dropdown.ListItem>
                                        {roles
                                            .sort((r1, r2) => {
                                                if (r1?.displayName > r2?.displayName) {
                                                    return 1;
                                                }

                                                if (r1?.displayName < r2?.displayName) {
                                                    return -1;
                                                }

                                                return 0;
                                            })
                                            .map((role) => {
                                                return (
                                                    <Dropdown.ListItem role="option" key={role.id}>
                                                        <Dropdown.BlockLink
                                                            isLink
                                                            onClick={() => roleFilterSelected(role.id)}
                                                        >
                                                            <Dropdown.BlockLinkText text={getRoleName(role)} />
                                                        </Dropdown.BlockLink>
                                                    </Dropdown.ListItem>
                                                );
                                            })}
                                    </Dropdown.Contents>
                                </Dropdown>
                            )}
                            {accessContext?.scope === CONTEXT_SCOPES.INSTANCE && isExp && (
                                <Dropdown
                                    className="push--left"
                                    buttonContent={{
                                        label: "Project",
                                        content: selectedProjectFilter
                                            ? projects?.find((p) => p.id === selectedProjectFilter)?.name
                                            : "All"
                                    }}
                                    style="plain"
                                    arrowIcon="down"
                                    width={350}
                                    isDisabled={noDefaultFilterUsers}
                                >
                                    <Dropdown.Contents>
                                        <Dropdown.ListItem role="option" key="All">
                                            <Dropdown.BlockLink isLink onClick={() => projectFilterSelected(undefined)}>
                                                <Dropdown.BlockLinkText text="All" />
                                            </Dropdown.BlockLink>
                                        </Dropdown.ListItem>
                                        {projects?.map((project) => (
                                            <Dropdown.ListItem role="option" key={project.id}>
                                                <Dropdown.BlockLink
                                                    isLink
                                                    onClick={() => projectFilterSelected(project.id)}
                                                >
                                                    <Dropdown.BlockLinkText text={project.name} />
                                                </Dropdown.BlockLink>
                                            </Dropdown.ListItem>
                                        ))}
                                    </Dropdown.Contents>
                                </Dropdown>
                            )}
                        </div>
                    </div>
                    <div className="flex">
                        {getBatchButtons()}
                        <InviteUserButtonAndSidebar />
                    </div>
                </div>

                {showActivationStatusesModal && <ActivationStatusesModal showModal={setShowActivationStatusesModal} />}

                {(debouncedSearchQuery.length > 2 || selectedRoleFilter || selectedProjectFilter) &&
                    !users?.length &&
                    !isLoading && (
                        <ResetFilterPrompt
                            onClick={() => {
                                setSearchQuery("");
                                setSelectedRoleFilter(undefined);
                                setSelectedProjectFilter(undefined);
                            }}
                            prompt="No users match your filter."
                        />
                    )}
                {noDefaultFilterUsers && <div>No users have access at this level</div>}
                <UnsavedChangesDialog />
                {!!selectedUser && (
                    <Sidebar heading={"User"} isOpen onCancel={handleFormCancel} noTransform>
                        <AccessManagementUserForm
                            onCancel={handleFormCancel}
                            organizationId={organizationId}
                            showModal={setShowActivationStatusesModal}
                            user={selectedUser}
                        />
                    </Sidebar>
                )}

                {isLoading ? (
                    <LoadingIndicator height="100%" type="spinner" />
                ) : (
                    <>
                        <UserTable
                            userList={users}
                            disableDeleteFromOptiId={disableDeleteFromOptiId}
                            organizationId={organizationId}
                            onViewDetails={(user) => updateAccessManagementUserState({ user, editing: false })}
                            onDeleteUsers={(users) => setUsersToDelete(users)}
                            onRemoveAccess={(users) => setUsersToRemoveAccess(users)}
                            onSelectionChange={({ users }: { users: User[] }) => setSelectedUsers(users)}
                            selectedUsers={selectedUsers}
                        />

                        {showPagination && (
                            <PaginationControls
                                className="anchor--bottom"
                                currentPage={currentPage}
                                goToPage={goToPage}
                                totalPages={totalPages}
                            />
                        )}
                    </>
                )}
            </div>
        </>
    );
};

AccessManagementUsers.displayName = "AccessManagementUsers";
